import React, { FC } from 'react';
import {
  type TypeBasicValuePropContainerFields,
  TypeMediaTextTitle,
  type TypeMediaTextTitleFields,
  TypeProduct,
} from '@/core/generated-types';
import { CarouselProducts } from '../Carousel';
import { DynamicComponent, HeaderText, LuvImage } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { usePathname } from '@/navigation';
import { UniversalCta } from '../UniversalCta';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import type { Entry } from 'contentful';
import ProductValueProps from '@/components/common/ProductAddToCart/ProductValueProps';

interface PlayKitInterface {
  product: TypeProduct;
  carousel: TypeMediaTextTitle[];
}

interface PlayKitBodyInterface {
  longDescription?: string;
  productValueProps?: Entry<
    TypeBasicValuePropContainerFields | TypeMediaTextTitleFields
  >[];
}

export const PlayKitDetailsBody: FC<PlayKitBodyInterface> = ({
  longDescription,
  productValueProps,
}) => {
  return (
    <div className="col-span-12">
      <p className="mb-4 leading-6">{longDescription}</p>
      <ProductValueProps
        productValueProps={productValueProps}
        shouldUsePlayKitStyles
      />
    </div>
  );
};

export const PlayKitDetailsHeader: FC<PlayKitInterface> = ({
  product,
  carousel,
}) => {
  const {
    sys: { id },
  } = product;
  const {
    badge,
    title,
    longDescription,
    productValueProps,
    featuredImage,
    shipMessage,
    cta,
    ageDescription,
  } = product.fields;
  const {
    api: { updateInitialProduct },
  } = useSubFlowV3Data();

  const pathname = usePathname();

  const handleClick = () => {
    updateInitialProduct(product);
  };

  return (
    <div className="mx-auto grid grid-cols-1 pb-4 md:bg-tan md:px-6 lg:grid-cols-12 lg:gap-2">
      <div className="lg:col-span-7 lg:hidden lg:p-12">
        <CarouselProducts
          name="pdp hero carousel"
          id={id}
          components={[featuredImage, ...carousel] as TypeMediaTextTitle[]}
          key={pathname}
        />
      </div>
      <div className="hidden lg:col-span-7 lg:block lg:p-12">
        <LuvImage imageData={featuredImage} height={800} width={800} />
      </div>
      <div className="col-span-full px-6 md:px-0 lg:col-span-5 lg:col-start-8 lg:pr-8">
        <div className="flex h-full flex-col justify-evenly">
          <div className="flex hidden lg:block">
            {badge && <DynamicComponent component={badge} />}
            <HeaderText text={title} size={HeaderSize.H1} />
            <p className="mb-2 text-base font-bold">{ageDescription}</p>
            <PlayKitDetailsBody
              longDescription={longDescription}
              productValueProps={productValueProps}
            />
            <div className="mx-auto mt-4 lg:mx-0 lg:mt-4">
              <div className="col-span-12">
                <p className="pb-2 text-base md:text-lg">{shipMessage}</p>
              </div>
              <div className="col-span-12">
                {cta && (
                  <UniversalCta
                    link={cta}
                    className="max-w-xs"
                    onClick={handleClick}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="mt-4 w-full lg:mx-0 lg:mt-0 lg:hidden">
            <div className="col-span-12">
              <p className="pb-2 text-center text-base md:text-lg">
                {shipMessage}
              </p>
            </div>
            {cta && <UniversalCta link={cta} />}
          </div>
        </div>
      </div>
    </div>
  );
};
